import React, { useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, AlertCircle, GripVertical } from 'lucide-react';

export function PoolsStep({ eventData, setEventData, errors }) {
  const initializePools = useCallback((numPools) => {
    const newPools = [];
    for (let i = 0; i < numPools; i++) {
      newPools.push({ id: `pool_${Date.now()}_${i}`, name: `Pool ${String.fromCharCode(65 + i)}`, team_ids: [] });
    }
    setEventData(prev => ({ ...prev, pool_config: { ...prev.pool_config, num_pools: numPools, pools: newPools } }));
  }, [setEventData]);

  const addTeamToPool = useCallback((teamId, poolId) => {
    setEventData(prev => {
      const updatedPools = prev.pool_config.pools.map(pool => ({ ...pool, team_ids: pool.team_ids.filter(id => id !== teamId) }));
      const idx = updatedPools.findIndex(p => p.id === poolId);
      if (idx !== -1) updatedPools[idx].team_ids.push(teamId);
      return { ...prev, pool_config: { ...prev.pool_config, pools: updatedPools } };
    });
  }, [setEventData]);

  const removeTeamFromPool = useCallback((teamId) => {
    setEventData(prev => ({
      ...prev,
      pool_config: { ...prev.pool_config, pools: prev.pool_config.pools.map(pool => ({ ...pool, team_ids: pool.team_ids.filter(id => id !== teamId) })) }
    }));
  }, [setEventData]);

  const unassignedTeams = useMemo(() => {
    if (!eventData.pool_config.enabled) return [];
    const assignedIds = new Set(eventData.pool_config.pools.flatMap(p => p.team_ids));
    return eventData.teams.filter(t => !assignedIds.has(t.id));
  }, [eventData.teams, eventData.pool_config]);

  return (
    <div className="space-y-6 wizard-step">
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
        <div>
          <Label className="text-slate-700 font-medium">Enable Pool Play</Label>
          <p className="text-sm text-slate-500">Divide teams into multiple pools for round-robin</p>
        </div>
        <Switch data-testid="enable-pools-switch" checked={eventData.pool_config.enabled}
          onCheckedChange={(checked) => {
            if (checked && eventData.pool_config.pools.length === 0) initializePools(2);
            setEventData(prev => ({ ...prev, pool_config: { ...prev.pool_config, enabled: checked } }));
          }} />
      </div>

      {eventData.pool_config.enabled && (
        <div className="space-y-6 animate-fade-in-up">
          <div className="form-field">
            <Label className="text-slate-700 font-medium">Number of Pools</Label>
            <Select value={eventData.pool_config.num_pools.toString()} onValueChange={(value) => initializePools(parseInt(value))}>
              <SelectTrigger data-testid="num-pools-select" className="mt-2 h-12 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2, 3, 4, 5, 6].filter(n => n <= Math.floor(eventData.teams.length / 2)).map(n => (
                  <SelectItem key={n} value={n.toString()}>{n} Pools</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {unassignedTeams.length > 0 && (
            <div className="form-field">
              <Label className="text-slate-700 font-medium mb-3 block">Unassigned Teams ({unassignedTeams.length})</Label>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {unassignedTeams.map((team) => (
                    <div key={team.id} className="px-3 py-2 bg-white border border-amber-300 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2 cursor-move">
                      <GripVertical className="w-4 h-4 text-slate-400" />{team.name}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 mt-3">Click a pool below to assign teams</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {eventData.pool_config.pools.map((pool, poolIndex) => {
              const poolTeams = eventData.teams.filter(t => pool.team_ids.includes(t.id));
              return (
                <div key={pool.id} data-testid={`pool-${poolIndex}`} className="p-4 border-2 border-slate-200 rounded-lg bg-white">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>{pool.name}</h4>
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">{poolTeams.length} teams</span>
                  </div>
                  <div className="space-y-2 min-h-[80px]">
                    {poolTeams.length === 0 ? (
                      <div className="h-20 flex items-center justify-center border-2 border-dashed border-slate-200 rounded-lg text-slate-400 text-sm">Click to add teams</div>
                    ) : (
                      poolTeams.map((team) => (
                        <div key={team.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg group">
                          <span className="text-sm font-medium text-slate-700">{team.name}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeTeamFromPool(team.id)}
                            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-slate-400 hover:text-red-500">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                  {unassignedTeams.length > 0 && (
                    <Select value="" onValueChange={(teamId) => addTeamToPool(teamId, pool.id)}>
                      <SelectTrigger data-testid={`add-to-pool-${poolIndex}`} className="mt-3 h-10">
                        <SelectValue placeholder="Add team to pool..." />
                      </SelectTrigger>
                      <SelectContent>
                        {unassignedTeams.map((team) => <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              );
            })}
          </div>

          {errors.pools && (
            <p className="text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-4 h-4" />{errors.pools}</p>
          )}

          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Pool Play Info:</strong> Teams in each pool will play round-robin matches against other teams in their pool.
              Top 2 teams from each pool will advance to playoffs.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
